import { z } from "zod";

import { DataParamsCodecMap } from "./dataParams";
import { formatTableStateSearchUrl } from "./formatTableStateSearchUrl";
import { isNoSaveUrl, NO_SAVE_PARAM_NAME } from "./noSave";
import {
    COLUMN_ORDER_PARAM_NAME,
    COLUMN_PINNING_PARAM_NAME,
    COLUMN_VISIBILITY_PARAM_NAME,
    TABLE_BASELINE_PARAM_NAME,
} from "./positionalColumnParams";
import { PresetSearchParamsCustomDefaultValues } from "./types";
import { COLUMN_FILTERS_PARAM_NAME } from "./usePresetTableSearchParams";
import { readTableStateStorage } from "./useTableStateStorage";

export const RESTORE_PARAM_NAME = "restore"; // mutually exclusive with `noSave`

const TABLE_STATE_PARAM_NAMES = [
    COLUMN_FILTERS_PARAM_NAME,
    "sorting",
    "globalFilter",
    COLUMN_ORDER_PARAM_NAME,
    COLUMN_VISIBILITY_PARAM_NAME,
    COLUMN_PINNING_PARAM_NAME,
    TABLE_BASELINE_PARAM_NAME,
];

export interface TableRestoreConfig {
    tableId: string;
    defaultValues: PresetSearchParamsCustomDefaultValues;
    codecMap: Record<string, z.ZodCodec>;
    onError?: (error: unknown) => void;
    dataParamsCodecMap?: DataParamsCodecMap;
}

export function isRestoreUrl(params: URLSearchParams): boolean {
    return params.has(RESTORE_PARAM_NAME);
}

export function withRestoreParam(path: string) {
    return `${path}${path.includes("?") ? "&" : "?"}${RESTORE_PARAM_NAME}` as const;
}

/**
 * The URL a `restore` URL settles on: the stored view encoded into the URL,
 * keeping any unrelated params. Explicit table params and `noSave` win over
 * the stored view, so then only the flag is dropped.
 */
export function resolveRestoreUrl(
    location: { pathname: string; search: string; hash: string },
    config: TableRestoreConfig
): string {
    const params = new URLSearchParams(location.search);
    params.delete(RESTORE_PARAM_NAME);
    const query = params.toString();
    const urlWithoutFlag = `${location.pathname}${query ? `?${query}` : ""}${location.hash}`;

    if (isNoSaveUrl(params)) {
        config.onError?.(
            new Error(
                `"${RESTORE_PARAM_NAME}" and "${NO_SAVE_PARAM_NAME}" are mutually exclusive, ignoring "${RESTORE_PARAM_NAME}" on ${location.pathname}`
            )
        );
        return urlWithoutFlag;
    }

    const ownedParamNames = [
        ...TABLE_STATE_PARAM_NAMES,
        ...Object.keys(config.dataParamsCodecMap ?? {}),
    ];
    if (ownedParamNames.some((name) => params.has(name))) {
        //Any filter params in the query params will override, the `restore` flag
        return urlWithoutFlag;
    }

    const [state, storedDataParams] = readTableStateStorage(config.tableId);
    // TODO: sc-39448 change to object and remember to adjust the consumers in legacy
    return formatTableStateSearchUrl(
        urlWithoutFlag,
        state,
        config.defaultValues,
        config.codecMap,
        config.onError,
        config.dataParamsCodecMap,
        { ...config.defaultValues.dataParams, ...storedDataParams }
    );
}
